<script setup lang="ts">
import { publicTicketApi } from '@libero/api-client/public-ticket/api';
import { SubscribeRequest } from '@libero/api-client/public-ticket/types';
import { useForm } from '@libero/hooks/useForm';
import Button from '@libero/ui-framework/Button/Button.vue';
import Checkbox from '@libero/ui-framework/Checkbox/Checkbox.vue';
import Cluster from '@libero/ui-framework/Cluster/Cluster.vue';
import Form from '@libero/ui-framework/Form/Form.vue';
import FormLayout from '@libero/ui-framework/FormLayout/FormLayout.vue';
import Input from '@libero/ui-framework/Input/Input.vue';
import InputGroup from '@libero/ui-framework/InputGroup/InputGroup.vue';
import Label from '@libero/ui-framework/Label/Label.vue';
import ModalActions from '@libero/ui-framework/ModalActions/ModalActions.vue';
import SubLabel from '@libero/ui-framework/SubLabel/SubLabel.vue';
import { useMutation } from '@tanstack/vue-query';
import { message } from 'ant-design-vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  ticketId: string;
  onClose: () => void;
}

const props = defineProps<Props>();

const { t } = useI18n();

const form = useForm<SubscribeRequest>(
  computed(() => ({
    email: null,
    accept_terms: false,
  })),
);

const { mutateAsync, isPending } = useMutation({
  mutationFn: publicTicketApi.subscribe(props.ticketId),
  onSuccess: () => {
    props.onClose();
    message.success(t('saved'));
  },
});

const handleSubmit = () => {
  form.submit(mutateAsync);
};
</script>

<template>
  <Form v-bind="form.registerForm(handleSubmit)">
    <FormLayout>
      <InputGroup>
        <Label for="email" :value="t('citizen-report.email')" size="xl" required />
        <SubLabel size="lg">{{ t('citizen-report.email-note') }}</SubLabel>
        <Input size="lg" v-bind="form.register('email')" autocomplete="email" />
      </InputGroup>

      <InputGroup>
        <Checkbox size="lg" v-bind="form.register('accept_terms')">
          {{ t('citizen-report.accept-terms') }}
        </Checkbox>
      </InputGroup>
    </FormLayout>

    <ModalActions>
      <Cluster justifyContent="end">
        <Button appearance="outline" :onClick="onClose">
          {{ t('cancel') }}
        </Button>

        <Button
          :label="t('report.subscribe')"
          type="submit"
          :isLoading="isPending"
          :isDisabled="!form.values.accept_terms"
        />
      </Cluster>
    </ModalActions>
  </Form>
</template>
